import { useCallback, useMemo, useState } from "react";
import type FileType from "src/constants/fileType";
import reportErrorSentry from "src/lib/reportErrorSentry";
import translate from "src/lib/translate";
import type {
  DocumentUploaderRef,
  SingleFileConfig
} from "src/ui/components/DocumentUploader/DocumentUploader";

export interface DocumentState {
  id: string;
  fileId: string | null; // null = not selected, "deferred-*" = selected but not uploaded, actual ID = uploaded
  status: "idle" | "uploading" | "success" | "error";
}

export interface UseDeferredDocumentUploadOptions {
  initialDocumentCount?: number;
  maxDocuments?: number;
  metaFileType: FileType;
  tags: string[];
  onUploadComplete?: () => void;
  onUploadError?: (error: unknown) => void;

  /**
   * Function to generate title for each document
   * @param index - The index of the document
   * @param hasFileId - Whether the document has a file selected
   */
  getTitleForDocument?: (index: number, hasFileId: boolean) => string;
}

export function useDeferredDocumentUpload(
  options: UseDeferredDocumentUploadOptions
) {
  const {
    initialDocumentCount = 1,
    maxDocuments,
    metaFileType,
    tags,
    onUploadComplete,
    onUploadError,
    getTitleForDocument = (index, hasFileId) =>
      index === 0 || hasFileId ? translate("uploadPage", { number: 1 }) : ""
  } = options;

  const [documents, setDocuments] = useState<DocumentState[]>(() =>
    Array.from({ length: initialDocumentCount }, (_, i) => ({
      id: `doc-${i}`,
      fileId: null,
      status: "idle" as const
    }))
  );

  const [isUploading, setIsUploading] = useState(false);

  const handleFileUploadChange = useCallback(
    (files: Record<string, string>) => {
      setDocuments((currentDocs) => {
        const updatedDocs = [...currentDocs];

        Object.entries(files).forEach(([docId, fileId]) => {
          if (fileId) {
            // Find existing document or add new one
            const existingIndex = updatedDocs.findIndex(
              (doc) => doc.id === docId
            );
            if (existingIndex >= 0) {
              // Update existing document
              updatedDocs[existingIndex] = {
                ...updatedDocs[existingIndex],
                fileId,
                status: fileId.startsWith("deferred-") ? "idle" : "success"
              };
            } else {
              // Add new document
              updatedDocs.push({
                id: docId,
                fileId,
                status: fileId.startsWith("deferred-") ? "idle" : "success"
              });
            }
          }
        });

        // Ensure we have at least one empty slot for new uploads (if not at max)
        const hasEmptySlot = updatedDocs.some((doc) => doc.fileId === null);
        const atMaxDocuments =
          maxDocuments !== undefined && updatedDocs.length >= maxDocuments;

        if (!hasEmptySlot && !atMaxDocuments) {
          updatedDocs.push({
            id: `doc-${updatedDocs.length}`,
            fileId: null,
            status: "idle"
          });
        }

        return updatedDocs;
      });
    },
    [maxDocuments]
  );

  const documentConfig = useMemo(() => {
    const docs: Record<string, SingleFileConfig> = {};

    documents.forEach((doc, index) => {
      docs[doc.id] = {
        initialFileId: doc.fileId ?? undefined,
        title: getTitleForDocument(index, Boolean(doc.fileId)),
        icon: !doc.fileId && index > 0 ? "plus" : undefined,
        metaFileType,
        tags
      };
    });

    return docs;
  }, [documents, metaFileType, tags, getTitleForDocument]);

  const handleContinue = useCallback(
    async (
      documentUploaderRef: React.RefObject<DocumentUploaderRef | null>,
      onSuccess?: (fileIds?: string[]) => void
    ) => {
      if (!documentUploaderRef.current) {
        onSuccess?.();
        return;
      }

      setDocuments((currentDocs) => {
        // Check if we have any selected files
        const selectedDocuments = currentDocs.filter(
          (doc) => doc.fileId !== null
        );
        if (selectedDocuments.length === 0) {
          onSuccess?.();
          return currentDocs;
        }

        // Check if all selected files are already uploaded
        const allAlreadyUploaded = selectedDocuments.every(
          (doc) => doc.fileId && !doc.fileId.startsWith("deferred-")
        );

        if (allAlreadyUploaded) {
          // All files already uploaded, just close
          const fileIds = selectedDocuments
            .filter((doc) => typeof doc.fileId === "string")
            .map((doc) => doc.fileId as string);

          onSuccess?.(fileIds);
          return currentDocs;
        }

        // Start upload process
        setIsUploading(true);

        // Mark uploading state
        const updatedDocs = currentDocs.map((doc) =>
          doc.fileId?.startsWith("deferred-")
            ? { ...doc, status: "uploading" as const }
            : doc
        );

        // Perform upload asynchronously
        documentUploaderRef.current
          ?.uploadDeferredFiles()
          .then((uploadedFiles) => {
            // Update state based on upload results
            setDocuments((prev) =>
              prev.map((doc) => {
                const uploadedFileId = uploadedFiles.get(doc.id);
                if (uploadedFileId) {
                  // This document was successfully uploaded
                  return { ...doc, fileId: uploadedFileId, status: "success" };
                } else if (doc.status === "uploading") {
                  // This document failed to upload
                  return { ...doc, status: "error" };
                }
                return doc;
              })
            );

            // Check if all selected files are now uploaded
            const allSuccessful = selectedDocuments.every((doc) => {
              const uploadedFileId = uploadedFiles.get(doc.id);
              return (
                uploadedFileId ??
                (doc.fileId && !doc.fileId.startsWith("deferred-"))
              );
            });

            if (allSuccessful) {
              const fileIds = Array.from(uploadedFiles.values()).filter(
                Boolean
              );
              onUploadComplete?.();
              onSuccess?.(fileIds);
            } else {
              // Some uploads failed, keep dialog open to show retry buttons
              setIsUploading(false);
            }
          })
          .catch((error: unknown) => {
            reportErrorSentry(error);
            onUploadError?.(error);
            setIsUploading(false);
          });

        return updatedDocs;
      });
    },
    [onUploadComplete, onUploadError]
  );

  const resetDocuments = useCallback(() => {
    setDocuments(
      Array.from({ length: initialDocumentCount }, (_, i) => ({
        id: `doc-${i}`,
        fileId: null,
        status: "idle" as const
      }))
    );
    setIsUploading(false);
  }, [initialDocumentCount]);

  const hasSelectedFiles = useMemo(
    () => documents.some((doc) => doc.fileId !== null),
    [documents]
  );

  const hasFailedUploads = useMemo(
    () => documents.some((doc) => doc.status === "error"),
    [documents]
  );

  const allUploadsSuccessful = useMemo(
    () =>
      documents.every(
        (doc) =>
          doc.status === "success" ||
          doc.fileId === null ||
          (doc.fileId && !doc.fileId.startsWith("deferred-"))
      ),
    [documents]
  );

  const selectedDocumentsCount = useMemo(
    () => documents.filter((doc) => doc.fileId !== null).length,
    [documents]
  );

  return {
    documents,
    isUploading,
    documentConfig,
    handleFileUploadChange,
    handleContinue,
    resetDocuments,
    setDocuments,
    hasSelectedFiles,
    hasFailedUploads,
    allUploadsSuccessful,
    selectedDocumentsCount
  };
}
